import React from 'react';
import AppImage from '@/components/ui/AppImage';

export type EraType = 'early' | 'mid' | 'recent';

export interface EpisodeCardData {
  id: string;
  number: number;
  title: string;
  guest?: string;
  pullQuote: string;
  thumbnail: string;
  thumbnailAlt: string;
  duration: string;
  era: EraType;
  tall?: boolean;
}

interface EpisodeCardProps {
  episode: EpisodeCardData;
  revealDelay?: number;
}

const eraThumbClass: Record<EraType, string> = {
  early:  'thumb-early',
  mid:    'thumb-mid',
  recent: 'thumb-recent',
};

const EpisodeCard: React.FC<EpisodeCardProps> = ({ episode, revealDelay = 0 }) => {
  const thumbClass = eraThumbClass[episode.era];
  const delayClass = revealDelay > 0 ? `reveal-delay-${Math.min(revealDelay, 5)}` : '';

  return (
    <div
      className={`episode-card torn-bottom reveal ${delayClass}`}
      style={{ borderRadius: '2px' }}
    >
      {/* Thumbnail */}
      <div
        className="relative overflow-hidden"
        style={{ height: episode.tall ? '220px' : '160px' }}
      >
        <AppImage
          src={episode.thumbnail}
          alt={episode.thumbnailAlt}
          fill
          className={`object-cover transition-transform duration-700 group-hover:scale-105 ${thumbClass}`}
        />
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-end justify-between p-3">
          <span
            className="font-body text-xs font-semibold px-2 py-1 rounded-sm"
            style={{
              background: 'rgba(245,240,232,0.88)',
              color: 'var(--color-charcoal)',
              backdropFilter: 'blur(4px)',
            }}
          >
            {episode.duration}
          </span>
          <div className="play-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4">
        <p className="ep-badge">
          Episode {episode.number}
          {episode.guest && (
            <span style={{ color: 'var(--color-brass)' }}>
              {' '}· {episode.guest}
            </span>
          )}
        </p>

        <h3
          className="font-display font-semibold text-base leading-snug mb-3"
          style={{ color: 'var(--color-charcoal)' }}
        >
          {episode.title}
        </h3>

        <p className="pull-quote text-sm leading-snug">
          {episode.pullQuote}
        </p>
      </div>
    </div>
  );
};

export default EpisodeCard;